// Disputes + Claims list — both nav routes render this component with a
// different `group` prop. The unified `app.disputes` table is bucketed into
// the two surfaces by `kind` (booking disputes vs insurance claims); see
// apps/api/src/modules/disputes/transitions.ts for the mapping.

import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import { PageHeader, ToolChip, ToolSearch, Toolbar } from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import {
  fetchAdminDisputes,
  type AdminDisputeRow,
  type AdminDisputesListParams,
  type DisputeStatus,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

type StatusFilter = DisputeStatus | 'open_only' | 'all';

const STATUS_FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: 'open_only', label: 'Open' },
  { key: 'evidence_pending', label: 'Evidence pending' },
  { key: 'under_review', label: 'Under review' },
  { key: 'escalated_to_carrier', label: 'Escalated' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'withdrawn', label: 'Withdrawn' },
  { key: 'all', label: 'All' },
];

function formatAmount(cents: number | null, currency: string | null): string {
  if (cents == null || currency == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const columns: DataTableColumn<AdminDisputeRow>[] = [
  {
    key: 'title',
    h: 'Dispute',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.title}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.bookingReference ? `Booking ${r.bookingReference}` : `Booking #${r.bookingId}`}
          {' · '}
          {r.kind}
        </div>
      </div>
    ),
  },
  {
    key: 'filer',
    h: 'Filed by',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate">{r.filedByName ?? r.filedByEmail ?? '—'}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.filedByRole}
        </div>
      </div>
    ),
  },
  {
    key: 'amount',
    h: 'Amount',
    cell: (r) => (
      <span className="tabular-nums">{formatAmount(r.amountCents, r.currency)}</span>
    ),
  },
  {
    key: 'status',
    h: 'Status',
    cell: (r) => (
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusPill status={r.status} />
        {r.resolution ? <StatusPill status={r.resolution} /> : null}
        {r.payoutHold ? <StatusPill status="on_hold" /> : null}
      </div>
    ),
  },
  {
    key: 'assignee',
    h: 'Assigned',
    cell: (r) =>
      r.assignedAdminEmail ? (
        <span className="truncate text-[12px]">{r.assignedAdminEmail}</span>
      ) : (
        <span className="text-[11.5px] text-[color:var(--bo-mute)]">Unassigned</span>
      ),
  },
  {
    key: 'opened',
    h: 'Opened',
    cell: (r) => <span className="tabular-nums text-[12px]">{formatDate(r.openedAt)}</span>,
  },
];

interface ListProps {
  group: 'dispute' | 'claim';
  eyebrow: string;
  title: string;
  sub: string;
}

function DisputesList({ group, eyebrow, title, sub }: ListProps) {
  const [status, setStatus] = useState<StatusFilter>('open_only');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();
  const navigate = useNavigate();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [status, q, setPage]);

  const params: AdminDisputesListParams = {
    group,
    status,
    q: q.trim() || undefined,
    page,
    limit,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'disputes', params],
    queryFn: ({ signal }) => fetchAdminDisputes(params, signal),
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        sub={`${total.toLocaleString()} ${title.toLowerCase()}${status === 'all' ? '' : ` · ${status.replace(/_/g, ' ')}`} · ${sub}`}
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load {title.toLowerCase()}: {(error as Error).message}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch
          value={q}
          onChange={setQ}
          placeholder="Search booking ref, filer email, title…"
        />
        {STATUS_FILTERS.map((f) => (
          <ToolChip key={f.key} active={status === f.key} onClick={() => setStatus(f.key)}>
            {f.label}
          </ToolChip>
        ))}
      </Toolbar>
      <DataTable
        columns={columns}
        rows={rows}
        emptyLabel={
          isLoading ? 'Loading…' : `No ${title.toLowerCase()} match these filters.`
        }
        onRowClick={(r) => void navigate({ to: `/disputes/${r.id}` as never })}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />
    </div>
  );
}

export function Disputes() {
  return (
    <DisputesList
      group="dispute"
      eyebrow="Trust"
      title="Disputes"
      sub="Damage / refund / cleanliness disputes filed against bookings."
    />
  );
}

export function Claims() {
  return (
    <DisputesList
      group="claim"
      eyebrow="Commerce"
      title="Insurance & claims"
      sub="Carrier-tracked claims with deductibles + payouts."
    />
  );
}
